import isObject from '../utility/isObject';
import * as moment from 'moment';
import ShipRegion from './shipRegion';
import ShipCityRegion from './shipCityRegion';

export type ShipRegionType = ShipRegion | ShipCityRegion;

class Store {
  public static fragmentsFields(): string {
    return `
      id
      name
      imageUrl
      email
      active
      zipCode
      region
      city
      address1
      address2
      cartUri
      paymentUri
      confirmUri
      completeUri
      businessOpenTime
      businessCloseTime
      regions {
        name
        cities {
          name
          addresses {
            name
          }
        }
      }
      cityRegions {
        name
        cities {
          name
        }
      }
      shipAreaType
      deliveryType
      ecLimitType
      ecLimitTime
      ecLimitBeforeDay
      signinUri
      signupUri
      resetPasswordUri
      inviteUri
      mypageUri
      topUri
      enableCartCredit
    `;
  }

  public static fromJson(value: any): Store {
    if (!isObject(value)) {
      throw new TypeError('value must be an object for Product.fromJson(value).');
    }

    const id = value['id'];
    const name = value['name'];
    const imageUrl = value['imageUrl'];
    const email = value['email'];
    const zipCode = value['zipCode'];
    const region = value['region'];
    const city = value['city'];
    const address1 = value['address1'];
    const address2 = value['address2'];
    const cartUri = value['cartUri'];
    const paymentUri = value['paymentUri'];
    const confirmUri = value['confirmUri'];
    const completeUri = value['completeUri'];
    const signinUri = value['signinUri'];
    const signupUri = value['signupUri'];
    const resetPasswordUri = value['resetPasswordUri'];
    const inviteUri = value['inviteUri'];
    const mypageUri = value['mypageUri'];
    const topUri = value['topUri'];
    const shipAreaType = value['shipAreaType'];
    const deliveryType = value['deliveryType'];
    const ecLimitType = value['ecLimitType'];
    const ecLimitBeforeDay = value['ecLimitBeforeDay'];
    const enableCartCredit = value['enableCartCredit'];
    const _businessOpenTime = value['businessOpenTime'];
    const _businessCloseTime = value['businessCloseTime'];
    const regions = value['regions'] ? value['regions'].map((regionData: any) => {
      return ShipRegion.fromJsonWithCity(regionData);
    }) : [];
    const cityRegions = value['cityRegions'] ? value['cityRegions'].map((regionData: any) => {
      return ShipCityRegion.fromJsonWithCity(regionData);
    }) : [];
    const _ecLimitTime = value['ecLimitTime'];

    if (typeof id !== 'string') {
      throw new TypeError('value.id must be a string for Product.fromJson(value).');
    }

    if (typeof name !== 'string') {
      throw new TypeError('value.name must be a string for Product.fromJson(value).');
    }

    const businessOpenTime = _businessOpenTime && moment(_businessOpenTime);
    const businessCloseTime = _businessCloseTime && moment(_businessCloseTime);
    const ecLimitTime = _ecLimitTime && moment(_ecLimitTime);

    return new Store({
      id,
      name,
      imageUrl,
      email,
      zipCode,
      region,
      city,
      address1,
      address2,
      cartUri,
      paymentUri,
      confirmUri,
      completeUri,
      businessOpenTime,
      businessCloseTime,
      regions,
      cityRegions,
      shipAreaType,
      deliveryType,
      ecLimitTime,
      ecLimitBeforeDay,
      ecLimitType,
      signinUri,
      signupUri,
      resetPasswordUri,
      inviteUri,
      mypageUri,
      topUri,
      enableCartCredit,
    });
  }

  public id: string;
  public name: string;
  public imageUrl: string;
  public email: string;
  public zipCode: string | undefined;
  public region: string | undefined;
  public city: string | undefined;
  public address1: string | undefined;
  public address2: string | undefined;
  public cartUri: string;
  public paymentUri: string;
  public confirmUri: string;
  public completeUri: string;
  public signinUri: string;
  public signupUri: string;
  public resetPasswordUri: string;
  public inviteUri: string;
  public mypageUri: string;
  public topUri: string;
  public shipAreaType: string;
  public ecLimitType: string;
  public ecLimitBeforeDay: number;
  public ecLimitTime: moment.Moment | undefined;
  public businessOpenTime: moment.Moment | undefined;
  public businessCloseTime: moment.Moment | undefined;
  public regions: ShipRegion[];
  public cityRegions: ShipCityRegion[];
  public deliveryType: string;
  public enableCartCredit: boolean;

  constructor({
    id = '',
    name = '',
    imageUrl = '',
    email ='',
    zipCode,
    region,
    city,
    address1,
    address2,
    cartUri = '',
    paymentUri = '',
    confirmUri = '',
    completeUri = '',
    mypageUri = '',
    signinUri = '',
    signupUri = '',
    resetPasswordUri = '',
    inviteUri = '',
    topUri = '',
    businessOpenTime,
    businessCloseTime,
    regions = [],
    cityRegions = [],
    shipAreaType = '',
    deliveryType = '',
    ecLimitType = 'limit_nothing',
    ecLimitBeforeDay = 0,
    ecLimitTime = undefined,
    enableCartCredit = false,
  }: {
    id?: string;
    name?: string;
    imageUrl?: string;
    email?: string;
    companyId?: string;
    password?: string;
    passwordConfirmation?: string;
    zipCode?: string;
    region?: string;
    city?: string;
    address1?: string;
    address2?: string;
    cartUri?: string;
    paymentUri?: string;
    confirmUri?: string;
    completeUri?: string;
    signinUri?: string;
    signupUri?: string;
    resetPasswordUri?: string;
    inviteUri?: string;
    mypageUri?: string;
    topUri?: string;
    shipAreaType?: string;
    businessOpenTime?: moment.Moment;
    businessCloseTime?: moment.Moment;
    regions?: ShipRegion[];
    cityRegions?: ShipCityRegion[];
    deliveryType?: string;
    ecLimitType?: string;
    ecLimitBeforeDay?: number;
    ecLimitTime?: moment.Moment;
    enableCartCredit?: boolean;
  }) {
    this.id = id;
    this.name = name;
    this.imageUrl = imageUrl;
    this.email = email;
    this.zipCode = zipCode;
    this.region = region;
    this.city = city;
    this.address1 = address1;
    this.address2 = address2;
    this.cartUri = cartUri;
    this.paymentUri = paymentUri;
    this.confirmUri = confirmUri;
    this.signinUri = signinUri;
    this.signupUri = signupUri;
    this.topUri = topUri;
    this.resetPasswordUri = resetPasswordUri;
    this.inviteUri = inviteUri;
    this.mypageUri = mypageUri;
    this.completeUri = completeUri;
    this.businessOpenTime = businessOpenTime;
    this.businessCloseTime = businessCloseTime;
    this.regions = regions;
    this.cityRegions = cityRegions;
    this.shipAreaType = shipAreaType;
    this.deliveryType = deliveryType;
    this.ecLimitType = ecLimitType;
    this.ecLimitBeforeDay = ecLimitBeforeDay;
    this.ecLimitTime = ecLimitTime;
    this.enableCartCredit = enableCartCredit;
  }

  // TODO: 複雑なので、リファクタリング
  public minOrderDate(): moment.Moment {
    const now = moment(new Date());
    const time: string | undefined = this.ecLimitTime && this.ecLimitTime.format('HH:mm');
    let openTime: string | undefined = undefined;
    if (this.businessOpenTime) {
      this.businessOpenTime.set({
        seconds: 0,
        milliseconds: 0,
      });
      openTime = this.businessOpenTime.format('HH:mm');
    }
    now.set({
      seconds: 0,
      milliseconds: 0,
    });

    if (this.ecLimitType === 'limit_nothing') {
      return moment(new Date());
    }
    if (this.ecLimitType === 'before_limit_date') {
      const limitDate = moment(new Date());
      limitDate.set({ seconds: 0, milliseconds: 0 });
      limitDate.add(this.ecLimitBeforeDay, 'd');

      if (time) {
        const validTimes = time.split(':');
        const validDate = now.clone().set({
          hours: parseInt(validTimes[0]),
          minutes: parseInt(validTimes[1]),
        });
        if (now.isAfter(validDate)) {
          limitDate.add(1, 'd');

          if (openTime) {
            const openTimes = openTime.split(':');

            limitDate.set({
              hours: parseInt(openTimes[0]),
              minutes: parseInt(openTimes[1]),
            });
          }
        } else {
          if (openTime) {
            const openTimes = openTime.split(':');
            validDate.set({
              hours: parseInt(openTimes[0]),
              minutes: parseInt(openTimes[1]),
            });
            limitDate.set({
              hours: parseInt(openTimes[0]),
              minutes: parseInt(openTimes[1]),
            });
          }
        }
      }

      return limitDate;
    }

    return moment(new Date());
  }

  public openDate(defaultShipDate?: Date) {
    if (this.businessOpenTime) {
      return this.convertNowDate(this.businessOpenTime, defaultShipDate);
    }
  }

  public closeDate(defaultShipDate?: Date) {
    if (this.businessCloseTime) {
      return this.convertNowDate(this.businessCloseTime, defaultShipDate);
    }
  }

  public regionsForType(): ShipRegionType[] {
    if (this.shipAreaType === 'address') {
      return this.regions;
    }

    if (this.shipAreaType === 'city') {
      return this.cityRegions;
    }

    return [];
  }

  private convertNowDate(date: moment.Moment, defaultShipDate?: Date) {
    const nowDate = defaultShipDate || new Date();
    nowDate.setHours(date.hours());
    nowDate.setMinutes(date.minutes());
    nowDate.setMilliseconds(0);

    return nowDate;
  }
}

export default Store;
